import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@deepguide-ai/ui/button';
import { clearHighlights, highlightQuote } from './pdf/pdfHighlight';
import { STANDARD_FONT_DATA_URL, getPdfJs } from './pdf/pdfjs-loader';
import './pdf/pdf-viewer.css';

interface PdfViewerProps {
  url: string;
  filename?: string;
  /** Signed storage URLs must stay unchanged and do not use login cookies. */
  presigned?: boolean;
  initialZoom?: number;
  pageNumber?: number | null;
  highlightText?: string;
  /** Page-relative box in 0–100 percentages */
  highlightBox?: { x: number; y: number; width: number; height: number; page?: number } | null;
}

type RenderedPage = {
  index: number; // 1-based
  wrapper: HTMLDivElement;
  textDivs: HTMLElement[];
};

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3.0;
const ZOOM_STEP = 0.25;
const SIDE_PADDING = 24;

export function PdfViewer({ url, filename, presigned = false, initialZoom = 1.0, pageNumber, highlightText, highlightBox }: PdfViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<import('pdfjs-dist').PDFDocumentProxy | null>(null);
  const renderedRef = useRef<RenderedPage[]>([]);
  const zoomRef = useRef(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, initialZoom)));
  const renderTokenRef = useRef(0);
  const lastScrollportWidthRef = useRef(0);

  const [zoom, setZoom] = useState(zoomRef.current);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scrollToPage = useCallback((pageNum: number) => {
    const entry = renderedRef.current[pageNum - 1];
    const scrollEl = scrollRef.current;
    if (!entry || !scrollEl) return;
    const wrapperRect = entry.wrapper.getBoundingClientRect();
    const containerRect = scrollEl.getBoundingClientRect();
    const targetTop = scrollEl.scrollTop + (wrapperRect.top - containerRect.top);
    scrollEl.scrollTo({ top: Math.max(0, targetTop), behavior: 'instant' as ScrollBehavior });
  }, []);

  const scrollToFirstHighlight = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const mark = pagesContainerRef.current?.querySelector<HTMLElement>('.pdf-text-highlight, .pdf-box-highlight');
    if (!mark) return;
    const containerRect = scrollEl.getBoundingClientRect();
    const markRect = mark.getBoundingClientRect();
    const offset = markRect.top - containerRect.top;
    const targetTop =
      scrollEl.scrollTop + offset - scrollEl.clientHeight / 2 + markRect.height / 2;
    scrollEl.scrollTo({ top: Math.max(0, targetTop), behavior: 'instant' as ScrollBehavior });
  }, []);

  // Render all pages — called on load + zoom change
  const renderPdf = useCallback(
    async (doc: import('pdfjs-dist').PDFDocumentProxy) => {
      const token = ++renderTokenRef.current;
      const container = pagesContainerRef.current;
      const scrollEl = scrollRef.current;
      if (!container || !scrollEl) return;

      container.innerHTML = '';
      renderedRef.current = [];
      const lib = await getPdfJs();

      const panelW = scrollEl.clientWidth;
      if (panelW <= 0) return;
      const firstPage = await doc.getPage(1);
      const naturalWidth = firstPage.getViewport({ scale: 1 }).width;
      const baseScale = Math.max(0.5, (panelW - SIDE_PADDING) / naturalWidth);
      const scale = baseScale * zoomRef.current;

      for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
        if (token !== renderTokenRef.current) return; // cancelled

        const page = await doc.getPage(pageNum);
        const viewport = page.getViewport({ scale });

        const wrapper = document.createElement('div');
        wrapper.className = 'pdf-page-wrapper';
        wrapper.style.width = `${viewport.width}px`;
        wrapper.style.height = `${viewport.height}px`;

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        wrapper.appendChild(canvas);
        container.appendChild(wrapper);

        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        try {
          await page.render({ canvasContext: ctx, viewport, standardFontDataUrl: STANDARD_FONT_DATA_URL }).promise;
        } catch (e) {
          if ((e as { name?: string })?.name !== 'RenderingCancelledException') {
            console.error('PDF render error', e);
          }
          continue;
        }

        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'pdf-text-layer';
        textLayerDiv.style.width = `${viewport.width}px`;
        textLayerDiv.style.height = `${viewport.height}px`;
        textLayerDiv.style.setProperty('--scale-factor', String(scale));
        wrapper.appendChild(textLayerDiv);

        const textLayer = new lib.TextLayer({
          textContentSource: page.streamTextContent(),
          container: textLayerDiv,
          viewport,
        });
        await textLayer.render();

        renderedRef.current.push({
          index: pageNum,
          wrapper,
          textDivs: textLayer.textDivs as HTMLElement[],
        });
      }
    },
    [],
  );

  // Load doc once
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    lastScrollportWidthRef.current = 0;

    (async () => {
      try {
        const lib = await getPdfJs();
        // Use the server's inline-streaming endpoint so the browser doesn't
        // follow the cross-origin redirect to the R2 presigned URL (which has
        // no CORS headers). withCredentials sends the auth cookie to our API.
        const inlineUrl = presigned ? url : url.includes('?') ? `${url}&inline=1` : `${url}?inline=1`;
        const task = lib.getDocument({
          url: inlineUrl,
          withCredentials: !presigned,
          standardFontDataUrl: STANDARD_FONT_DATA_URL,
        });
        const doc = await task.promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        await renderPdf(doc);
        if (cancelled) return;
        if (scrollRef.current) {
          lastScrollportWidthRef.current = scrollRef.current.clientWidth;
        }
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          console.error('PdfViewer load error', e);
          setError(e instanceof Error ? e.message : 'Failed to load PDF');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, presigned, renderPdf]);

  // Dialog flex layout may paint the scrollport at 0px before the first render.
  useEffect(() => {
    const scrollEl = scrollRef.current;
    const doc = pdfDocRef.current;
    if (!scrollEl || !doc || loading) return;

    const tryRerender = () => {
      const w = scrollEl.clientWidth;
      if (w <= 0 || w === lastScrollportWidthRef.current) return;
      lastScrollportWidthRef.current = w;
      void renderPdf(doc);
    };

    tryRerender();
    const ro = new ResizeObserver(tryRerender);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [loading, renderPdf, url]);

  // Apply highlights & jump to page when props change or after render
  useEffect(() => {
    if (loading || !renderedRef.current.length) return;

    // Clear all
    for (const p of renderedRef.current) clearHighlights(p.textDivs);
    pagesContainerRef.current?.querySelectorAll('.pdf-box-highlight').forEach((el) => el.remove());

    const trimmed = highlightText?.trim();
    let firstHitPage: number | null = null;

    if (trimmed) {
      // Try the hinted page first (highlightBox.page or pageNumber), else scan all
      const hinted = highlightBox?.page ?? pageNumber ?? null;
      if (hinted) {
        const target = renderedRef.current[hinted - 1];
        if (target && highlightQuote(target.textDivs, trimmed)) firstHitPage = hinted;
      }
      if (firstHitPage === null) {
        for (const p of renderedRef.current) {
          if (highlightQuote(p.textDivs, trimmed)) {
            firstHitPage = p.index;
            break;
          }
        }
      }
    }

    if (highlightBox) {
      const targetPage = highlightBox.page ?? pageNumber ?? 1;
      const entry = renderedRef.current[targetPage - 1];
      if (entry) {
        const box = document.createElement('div');
        box.className = 'pdf-box-highlight';
        box.style.left = `${highlightBox.x}%`;
        box.style.top = `${highlightBox.y}%`;
        box.style.width = `${highlightBox.width}%`;
        box.style.height = `${highlightBox.height}%`;
        entry.wrapper.appendChild(box);
        if (firstHitPage === null) firstHitPage = targetPage;
      }
    }

    if (firstHitPage !== null) {
      // wait for layout
      requestAnimationFrame(() => scrollToFirstHighlight());
    } else if (pageNumber && pageNumber >= 1) {
      requestAnimationFrame(() => scrollToPage(pageNumber));
    }
  }, [loading, highlightText, highlightBox, pageNumber, scrollToFirstHighlight, scrollToPage]);

  // Track current page on scroll
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const onScroll = () => {
      const pages = renderedRef.current;
      if (!pages.length) return;
      const center = scrollEl.scrollTop + scrollEl.clientHeight / 2;
      let closest = 0;
      let closestDist = Infinity;
      pages.forEach((p, i) => {
        const pageCenter = p.wrapper.offsetTop + p.wrapper.clientHeight / 2;
        const dist = Math.abs(pageCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      setCurrentPage(closest + 1);
    };
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [loading]);

  const adjustZoom = (next: number) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next * 100) / 100));
    if (clamped === zoomRef.current) return;
    zoomRef.current = clamped;
    setZoom(clamped);
    if (pdfDocRef.current) {
      const remembered = currentPage;
      void renderPdf(pdfDocRef.current).then(() => {
        // restore page after rerender
        if (renderedRef.current.length >= remembered) {
          requestAnimationFrame(() => scrollToPage(remembered));
        }
      });
    }
  };

  return (
    <div className="flex h-full w-full flex-col rounded-md border bg-surface-subtle overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-card px-3 py-1.5">
        <div className="flex items-center gap-1 text-[12px] text-text-body">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={currentPage <= 1}
            onClick={() => scrollToPage(currentPage - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="tabular-nums">
            {currentPage} / {numPages || '–'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={currentPage >= numPages}
            onClick={() => scrollToPage(currentPage + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={zoom <= ZOOM_MIN}
            onClick={() => adjustZoom(zoom - ZOOM_STEP)}
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="tabular-nums text-[11px] text-text-subtle w-10 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={zoom >= ZOOM_MAX}
            onClick={() => adjustZoom(zoom + ZOOM_STEP)}
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          {filename && (
            <Button variant="ghost" size="icon" className="h-7 w-7 ml-1" asChild aria-label="Download">
              <a href={url} download={filename}>
                <Download className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain"
      >
        {error && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-8 text-center">
            <span className="text-sm text-status-red">{error}</span>
            {filename && (
              <Button variant="outline" size="sm" asChild>
                <a href={url} download={filename}>
                  Download
                </a>
              </Button>
            )}
          </div>
        )}
        {loading && !error && (
          <div className="flex h-full w-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading PDF…
          </div>
        )}
        <div
          ref={pagesContainerRef}
          className={`mx-auto flex w-max min-w-full flex-col items-center gap-2 py-3 ${loading || error ? 'hidden' : ''}`}
        />
      </div>
    </div>
  );
}
